import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Loader2, Merge, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function VariantOption({ v, selected, onSelect, testid }) {
  return (
    <label data-testid={testid}
      className={`flex gap-3 rounded-md border p-3 cursor-pointer transition-colors ${selected ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`}>
      <input type="radio" className="mt-1 accent-[hsl(var(--primary))]" checked={selected} onChange={onSelect} />
      <div className="min-w-0 text-sm">
        <div className="font-medium">{v.kunde_name || "– ohne Name –"}</div>
        <div className="text-xs text-muted-foreground">{[v.strasse, [v.kunde_plz, v.kunde_ort].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "keine Adresse"}</div>
        {(v.email || v.telefon) && <div className="text-xs text-muted-foreground">{[v.email, v.telefon].filter(Boolean).join(" · ")}</div>}
        <div className="text-[11px] mt-1 text-muted-foreground">
          {v.projects.length} Projekt(e) · {v.anmeldungen.length} Anmeldung(en){v.auftraggeber.length ? ` · ${v.auftraggeber.join(", ")}` : ""}
        </div>
      </div>
    </label>
  );
}

function DuplicateGroup({ group, idx, onDone }) {
  const [master, setMaster] = useState(0);
  const [busy, setBusy] = useState(false);
  const merge = async () => {
    const m = group.variants[master];
    setBusy(true);
    try {
      const { data } = await api.post("/kunden/duplikate/merge", {
        master: m.master,
        project_ids: group.variants.flatMap((v) => v.projects),
        anmeldung_ids: group.variants.flatMap((v) => v.anmeldungen),
      });
      toast.success(`Zusammengeführt zu „${data.kunde_name}“ (${data.projects_updated} Projekte, ${data.anmeldungen_updated} Anmeldungen)`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const ignore = async () => {
    try { await api.post("/kunden/duplikate/ignore", { signature: group.signature }); toast.success("Als „kein Duplikat“ markiert"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-lg border border-border p-4 space-y-3" data-testid={`dup-group-${idx}`}>
      <div className="text-xs text-muted-foreground">Welcher Eintrag ist korrekt? Alle anderen übernehmen Name, Adresse und Kontaktdaten.</div>
      <div className="grid gap-2 sm:grid-cols-2">
        {group.variants.map((v, i) => (
          <VariantOption key={v.key} v={v} selected={master === i} onSelect={() => setMaster(i)} testid={`dup-group-${idx}-variant-${i}`} />
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={ignore} data-testid={`dup-group-${idx}-ignore`}><EyeOff className="h-4 w-4 mr-1" />Kein Duplikat</Button>
        <Button size="sm" onClick={merge} disabled={busy} data-testid={`dup-group-${idx}-merge`}>
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Merge className="h-4 w-4 mr-1" />}Zusammenführen
        </Button>
      </div>
    </div>
  );
}

export function DuplicateFinder({ onMerged }) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState(null);
  const load = async () => {
    setGroups(null);
    try { setGroups((await api.get("/kunden/duplikate")).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setGroups([]); }
  };
  useEffect(() => { if (open) load(); }, [open]);
  const done = () => { load(); onMerged?.(); };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="kunden-duplikate-btn">
        <Copy className="h-4 w-4 mr-1" /> Duplikate prüfen
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="duplikate-dialog">
          <DialogHeader>
            <DialogTitle>Doppelte Kunden</DialogTitle>
            <DialogDescription>Erkannt über ähnliche Namen, gleiche Adresse/PLZ, E-Mail oder Telefonnummer – über Projekte und Netzanmeldungen hinweg.</DialogDescription>
          </DialogHeader>
          {groups === null && <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {groups?.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="duplikate-empty">
              <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-emerald-500" />Keine möglichen Duplikate gefunden.
            </div>
          )}
          {groups?.length > 0 && (
            <div className="space-y-4">
              <div className="text-sm" data-testid="duplikate-count">{groups.length} mögliche Duplikat-Gruppe(n)</div>
              {groups.map((g, i) => <DuplicateGroup key={g.signature} group={g} idx={i} onDone={done} />)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
